import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { Plus, Search, Save, Package } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { Asset, AssetAssignment, AssetMaintenance } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { LoadingState } from "@/components/shared/StatePanels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type Tab = "list" | "detail" | "create";

const CATEGORIES = ["Vehicle", "Motorcycle", "Equipment", "Computer", "Pump", "Generator", "Other"];
const STATUS_COLORS: Record<string, string> = {
  AVAILABLE: "border-primary/20 bg-primary/10 text-primary",
  ASSIGNED: "border-blue-500/20 bg-blue-500/10 text-blue-600",
  MAINTENANCE: "border-amber-500/20 bg-amber-500/10 text-amber-600",
  LOST: "border-red-500/20 bg-red-500/10 text-red-600",
  RETIRED: "border-muted/20 bg-muted/10 text-muted-foreground",
  ARCHIVED: "border-muted/20 bg-muted/10 text-muted-foreground",
};

export default function AssetsPage() {
  const [tab, setTab] = useState<Tab>("list");
  const [assets, setAssets] = useState<Asset[]>();
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [assignments, setAssignments] = useState<AssetAssignment[]>([]);
  const [maintenance, setMaintenance] = useState<AssetMaintenance[]>([]);
  const [search, setSearch] = useState("");
  const [detailTab, setDetailTab] = useState<"info" | "assignments" | "maintenance">("info");
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [maintaining, setMaintaining] = useState(false);
  const [assetForm, setAssetForm] = useState({ asset_code: "", asset_name: "", asset_category: "Vehicle", serial_number: "", acquisition_date: "", acquisition_cost: "", currency: "UGX" });
  const [assignForm, setAssignForm] = useState({ employee_id: "", geographic_unit_id: "", assigned_at: new Date().toISOString().slice(0, 16), expected_return_at: "", received_condition: "" });
  const [maintForm, setMaintForm] = useState({ maintenance_date: "", maintenance_type: "", description: "", outcome: "", performed_by: "", next_service_date: "", cost: "" });
  const [errors, setErrors] = useState<string[]>([]);

  const loadAssets = async () => {
    try {
      const result = await services.assets.list({ pageSize: 100 });
      setAssets(result.items);
    } catch { setAssets([]); }
  };

  useEffect(() => { loadAssets(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return assets || [];
    return (assets || []).filter((a) => `${a.assetCode} ${a.assetName} ${a.assetCategory} ${a.status}`.toLowerCase().includes(q));
  }, [assets, search]);

  if (!assets) return <LoadingState label="Loading asset register" />;

  const showDetail = async (asset: Asset) => {
    setSelectedAsset(asset);
    setTab("detail");
    setDetailTab("info");
    try {
      const [a, m] = await Promise.all([
        services.assets.listAssignments(asset.id),
        services.assets.listMaintenance(asset.id),
      ]);
      setAssignments(a.items);
      setMaintenance(m.items);
    } catch { setAssignments([]); setMaintenance([]); }
  };

  const submitAsset = async (e: FormEvent) => {
    e.preventDefault();
    const errs = [
      !assetForm.asset_code && "Asset code is required.",
      !assetForm.asset_name && "Asset name is required.",
    ].filter(Boolean) as string[];
    setErrors(errs);
    if (errs.length) return;
    try {
      const created = await services.assets.create({
        asset_code: assetForm.asset_code,
        asset_name: assetForm.asset_name,
        asset_category: assetForm.asset_category,
        serial_number: assetForm.serial_number || undefined,
        acquisition_date: assetForm.acquisition_date || undefined,
        acquisition_cost: assetForm.acquisition_cost ? Number(assetForm.acquisition_cost) : undefined,
        currency: assetForm.currency || undefined,
      });
      toast("Asset registered", { description: `${created.assetCode} — ${created.assetName}` });
      setCreating(false);
      setAssetForm({ asset_code: "", asset_name: "", asset_category: "Vehicle", serial_number: "", acquisition_date: "", acquisition_cost: "", currency: "UGX" });
      await loadAssets();
      setTab("list");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to register asset";
      toast("Error", { description: msg });
    }
  };

  const submitAssignment = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedAsset) return;
    try {
      await services.assets.createAssignment(selectedAsset.id, {
        employee_id: assignForm.employee_id || undefined,
        geographic_unit_id: assignForm.geographic_unit_id || undefined,
        assigned_at: new Date(assignForm.assigned_at).toISOString(),
        expected_return_at: assignForm.expected_return_at ? new Date(assignForm.expected_return_at).toISOString() : undefined,
        received_condition: assignForm.received_condition || undefined,
      });
      toast("Asset assigned", { description: `${selectedAsset.assetCode} has been assigned` });
      setAssigning(false);
      const a = await services.assets.listAssignments(selectedAsset.id);
      setAssignments(a.items);
      const updated = await services.assets.get(selectedAsset.id);
      if (updated) setSelectedAsset(updated);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to assign asset";
      toast("Error", { description: msg });
    }
  };

  const submitReturn = async (assignmentId: string) => {
    if (!selectedAsset) return;
    try {
      await services.assets.returnAssignment(selectedAsset.id, assignmentId, {
        returned_at: new Date().toISOString(),
        returned_condition: "Good",
      });
      toast("Asset returned", { description: `${selectedAsset.assetCode} has been returned` });
      const a = await services.assets.listAssignments(selectedAsset.id);
      setAssignments(a.items);
      const updated = await services.assets.get(selectedAsset.id);
      if (updated) setSelectedAsset(updated);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to return asset";
      toast("Error", { description: msg });
    }
  };

  const submitMaintenance = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedAsset) return;
    try {
      await services.assets.createMaintenance(selectedAsset.id, {
        maintenance_date: maintForm.maintenance_date,
        maintenance_type: maintForm.maintenance_type,
        description: maintForm.description || undefined,
        outcome: maintForm.outcome || undefined,
        performed_by: maintForm.performed_by || undefined,
        next_service_date: maintForm.next_service_date || undefined,
        cost: maintForm.cost ? Number(maintForm.cost) : undefined,
      });
      toast("Maintenance recorded", { description: `${selectedAsset.assetCode} service log updated` });
      setMaintaining(false);
      const m = await services.assets.listMaintenance(selectedAsset.id);
      setMaintenance(m.items);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to record maintenance";
      toast("Error", { description: msg });
    }
  };

  if (tab === "detail" && selectedAsset) {
    return (
      <div className="space-y-6" data-testid="assets-detail-page">
        <PageHeader eyebrow="Asset register" title={selectedAsset.assetName} description={`${selectedAsset.assetCode} · ${selectedAsset.assetCategory}`} action={<Button variant="outline" onClick={() => setTab("list")} data-testid="asset-back-button">Back to list</Button>} />
        <div className="flex gap-2">
          <Button variant={detailTab === "info" ? "default" : "outline"} size="sm" onClick={() => setDetailTab("info")}>Info</Button>
          <Button variant={detailTab === "assignments" ? "default" : "outline"} size="sm" onClick={() => setDetailTab("assignments")}>Assignments ({assignments.length})</Button>
          <Button variant={detailTab === "maintenance" ? "default" : "outline"} size="sm" onClick={() => setDetailTab("maintenance")}>Maintenance ({maintenance.length})</Button>
        </div>
        {detailTab === "info" && (
          <Card><CardContent className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Asset code:</span> <span className="font-mono">{selectedAsset.assetCode}</span></div>
              <div><span className="text-muted-foreground">Category:</span> {selectedAsset.assetCategory}</div>
              <div><span className="text-muted-foreground">Serial number:</span> {selectedAsset.serialNumber || "—"}</div>
              <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className={STATUS_COLORS[selectedAsset.status]}>{selectedAsset.status}</Badge></div>
              <div><span className="text-muted-foreground">Acquisition date:</span> {selectedAsset.acquisitionDate || "—"}</div>
              <div><span className="text-muted-foreground">Acquisition cost:</span> {selectedAsset.acquisitionCost ? `${selectedAsset.acquisitionCost} ${selectedAsset.currency || ""}` : "—"}</div>
              <div><span className="text-muted-foreground">Location:</span> {selectedAsset.currentGeographicUnitLabel || "—"}</div>
              <div><span className="text-muted-foreground">Row version:</span> {selectedAsset.rowVersion}</div>
            </div>
            {selectedAsset.currentAssignment && (
              <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-sm">
                <span className="font-semibold">Currently assigned to:</span> {selectedAsset.currentAssignment.employeeName || "Location"} (since {new Date(selectedAsset.currentAssignment.assignedAt).toLocaleDateString()})
              </div>
            )}
          </CardContent></Card>
        )}
        {detailTab === "assignments" && (
          <Card><CardContent className="p-0">
            <div className="flex items-center justify-between border-b border-border p-4">
              <h3 className="font-semibold">Assignment history</h3>
              <Button size="sm" onClick={() => setAssigning(!assigning)} disabled={selectedAsset.status === "ASSIGNED"} data-testid="asset-assign-button"><Plus className="mr-1 size-4" />Assign</Button>
            </div>
            {assigning && (
              <form onSubmit={submitAssignment} className="space-y-3 border-b border-border p-4" data-testid="asset-assign-form">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1"><Label>Employee ID</Label><Input value={assignForm.employee_id} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssignForm({ ...assignForm, employee_id: e.target.value })} placeholder="UUID (optional)" /></div>
                  <div className="space-y-1"><Label>Geographic unit ID</Label><Input value={assignForm.geographic_unit_id} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssignForm({ ...assignForm, geographic_unit_id: e.target.value })} placeholder="UUID (optional)" /></div>
                  <div className="space-y-1"><Label>Assigned at</Label><Input type="datetime-local" value={assignForm.assigned_at} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssignForm({ ...assignForm, assigned_at: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Expected return</Label><Input type="datetime-local" value={assignForm.expected_return_at} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssignForm({ ...assignForm, expected_return_at: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Received condition</Label><Input value={assignForm.received_condition} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssignForm({ ...assignForm, received_condition: e.target.value })} placeholder="e.g. Good" /></div>
                </div>
                <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setAssigning(false)}>Cancel</Button><Button type="submit"><Save className="mr-1 size-4" />Assign</Button></div>
              </form>
            )}
            <Table><TableHeader><TableRow><TableHead>Custodian</TableHead><TableHead>Assigned at</TableHead><TableHead>Returned at</TableHead><TableHead>Condition</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{assignments.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.employeeName || a.geographicUnitId || "—"}</TableCell>
                  <TableCell className="text-xs">{new Date(a.assignedAt).toLocaleDateString()}</TableCell>
                  <TableCell className="text-xs">{a.returnedAt ? new Date(a.returnedAt).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-xs">{a.receivedCondition || "—"} / {a.returnedCondition || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={a.status === "ACTIVE" ? "border-blue-500/20 bg-blue-500/10 text-blue-600" : ""}>{a.status}</Badge></TableCell>
                  <TableCell>{a.status === "ACTIVE" && <Button size="sm" variant="outline" onClick={() => submitReturn(a.id)} data-testid={`return-assignment-${a.id}`}>Return</Button>}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </CardContent></Card>
        )}
        {detailTab === "maintenance" && (
          <Card><CardContent className="p-0">
            <div className="flex items-center justify-between border-b border-border p-4">
              <h3 className="font-semibold">Maintenance history</h3>
              <Button size="sm" onClick={() => setMaintaining(!maintaining)} data-testid="asset-maintenance-button"><Plus className="mr-1 size-4" />Add service</Button>
            </div>
            {maintaining && (
              <form onSubmit={submitMaintenance} className="space-y-3 border-b border-border p-4" data-testid="asset-maintenance-form">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1"><Label>Date</Label><Input type="date" value={maintForm.maintenance_date} onChange={(e: ChangeEvent<HTMLInputElement>) => setMaintForm({ ...maintForm, maintenance_date: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Type</Label><Input value={maintForm.maintenance_type} onChange={(e: ChangeEvent<HTMLInputElement>) => setMaintForm({ ...maintForm, maintenance_type: e.target.value })} placeholder="e.g. Service, Repair" /></div>
                  <div className="space-y-1"><Label>Performed by</Label><Input value={maintForm.performed_by} onChange={(e: ChangeEvent<HTMLInputElement>) => setMaintForm({ ...maintForm, performed_by: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Description</Label><Input value={maintForm.description} onChange={(e: ChangeEvent<HTMLInputElement>) => setMaintForm({ ...maintForm, description: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Outcome</Label><Input value={maintForm.outcome} onChange={(e: ChangeEvent<HTMLInputElement>) => setMaintForm({ ...maintForm, outcome: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Next service</Label><Input type="date" value={maintForm.next_service_date} onChange={(e: ChangeEvent<HTMLInputElement>) => setMaintForm({ ...maintForm, next_service_date: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Cost</Label><Input type="number" value={maintForm.cost} onChange={(e: ChangeEvent<HTMLInputElement>) => setMaintForm({ ...maintForm, cost: e.target.value })} /></div>
                </div>
                <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setMaintaining(false)}>Cancel</Button><Button type="submit"><Save className="mr-1 size-4" />Record</Button></div>
              </form>
            )}
            <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Performed by</TableHead><TableHead>Outcome</TableHead><TableHead>Next service</TableHead></TableRow></TableHeader>
              <TableBody>{maintenance.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="text-xs">{m.maintenanceDate}</TableCell>
                  <TableCell>{m.maintenanceType}</TableCell>
                  <TableCell className="text-xs">{m.performedBy || "—"}</TableCell>
                  <TableCell className="text-xs">{m.outcome || "—"}</TableCell>
                  <TableCell className="text-xs">{m.nextServiceDate || "—"}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </CardContent></Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assets-page">
      <PageHeader eyebrow="Asset management" title="Assets" description="Register, assign, and track equipment, vehicles, and field devices." action={<Button onClick={() => { setTab("create"); setCreating(true); }} data-testid="asset-create-button"><Plus className="mr-2 size-4" />Register asset</Button>} />
      {tab === "create" && creating && (
        <Card data-testid="asset-create-panel"><CardContent className="p-5">
          <form onSubmit={submitAsset} className="space-y-5" data-testid="asset-create-form">
            {errors.length > 0 && <div className="rounded-lg border border-destructive/30 p-3 text-sm text-destructive">{errors.join(" ")}</div>}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2"><Label htmlFor="asset-code">Asset code / tag</Label><Input id="asset-code" value={assetForm.asset_code} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssetForm({ ...assetForm, asset_code: e.target.value })} placeholder="e.g. AST-MOTO-002" data-testid="asset-code-input" /></div>
              <div className="space-y-2"><Label htmlFor="asset-name">Asset name</Label><Input id="asset-name" value={assetForm.asset_name} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssetForm({ ...assetForm, asset_name: e.target.value })} placeholder="e.g. Field motorcycle 02" data-testid="asset-name-input" /></div>
              <div className="space-y-2"><Label>Category</Label>
                <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={assetForm.asset_category} onChange={(e: ChangeEvent<HTMLSelectElement>) => setAssetForm({ ...assetForm, asset_category: e.target.value })} data-testid="asset-category-select">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2"><Label htmlFor="asset-serial">Serial / registration</Label><Input id="asset-serial" value={assetForm.serial_number} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssetForm({ ...assetForm, serial_number: e.target.value })} data-testid="asset-serial-input" /></div>
              <div className="space-y-2"><Label htmlFor="asset-acq-date">Acquisition date</Label><Input id="asset-acq-date" type="date" value={assetForm.acquisition_date} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssetForm({ ...assetForm, acquisition_date: e.target.value })} /></div>
              <div className="space-y-2"><Label htmlFor="asset-acq-cost">Acquisition cost</Label><Input id="asset-acq-cost" type="number" value={assetForm.acquisition_cost} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssetForm({ ...assetForm, acquisition_cost: e.target.value })} /></div>
              <div className="space-y-2"><Label htmlFor="asset-currency">Currency</Label><Input id="asset-currency" value={assetForm.currency} onChange={(e: ChangeEvent<HTMLInputElement>) => setAssetForm({ ...assetForm, currency: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => { setCreating(false); setTab("list"); }}>Cancel</Button><Button type="submit" data-testid="asset-submit-button"><Save className="mr-2 size-4" />Register</Button></div>
          </form>
        </CardContent></Card>
      )}
      {tab === "list" && (
        <Card className="border-border/70 shadow-soft" data-testid="assets-table-card"><CardContent className="p-0">
          <div className="flex items-center gap-3 border-b border-border p-4">
            <div className="relative flex-1"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} placeholder="Search code, name, category, or status" className="pl-9" data-testid="assets-search-input" /></div>
            <Badge variant="secondary" data-testid="assets-count-badge"><Package className="mr-1 size-3" />{filtered.length} assets</Badge>
          </div>
          <Table data-testid="assets-table"><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Serial</TableHead><TableHead>Status</TableHead><TableHead>Current custodian</TableHead></TableRow></TableHeader>
            <TableBody>{filtered.map((asset) => (
              <TableRow key={asset.id} className="cursor-pointer hover:bg-muted/50" onClick={() => showDetail(asset)} data-testid={`asset-row-${asset.id}`}>
                <TableCell className="font-mono text-xs">{asset.assetCode}</TableCell>
                <TableCell className="font-semibold text-primary">{asset.assetName}</TableCell>
                <TableCell className="text-muted-foreground">{asset.assetCategory}</TableCell>
                <TableCell className="font-mono text-xs">{asset.serialNumber || "—"}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_COLORS[asset.status]}>{asset.status}</Badge></TableCell>
                <TableCell className="text-xs">{asset.currentAssignment?.employeeName || "—"}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
          {filtered.length === 0 && <div className="px-4 py-12 text-center"><p className="font-semibold">No assets found</p><p className="mt-1 text-sm text-muted-foreground">Register an asset to get started.</p></div>}
        </CardContent></Card>
      )}
    </div>
  );
}
